import Vue from 'vue';
import { Component, Watch } from 'vue-property-decorator';

import {
  CustomerProfile,
  Promotion,
  RecentOrFavoriteOrder,
  PreConfiguredMeal,
  PromoBanner,
  LargeOrderPromo,
  LargeOrderPromoCard,
  OrderType,
  MealType,
} from '@/models';
import { ui } from '@/constants';
import { CountryCode } from '@/store/ui';
import { GETTERS as CUSTOMER_GETTERS } from '@/store/customer';
import { isLoyaltyEnabled } from '@/helpers/country';
import { ExternalCampaignEvent, HomePageLoadEvent } from '@/services/analytics/events';
import { AEMHomepageXfs, getAemXfHtml } from '@/models/aem';

@Component({
  name: 'CmgHome',
})
export default class CmgHome extends Vue {
  watchers: (() => void)[] = [];
  private hasScrolledToHash: boolean = false;
  private isLoadingFavorites: boolean = false;
  private isLoadingRecent: boolean = false;
  private isLoadingAemXfs: boolean = false;
  private isAemHeroXFMissing: boolean = false;

  // #region Common
  get isDesktop(): boolean {
    return this.$appStore.state.ui.clientWidth > ui.breakpoints.tabletMax;
  }

  get isLoggedIn(): boolean {
    return this.$services.customer.isLoggedIn() === true;
  }

  get customer(): CustomerProfile | null {
    return this.$appStore.state.customer.profile;
  }

  // #endregion

  // #region Recents & Favorites
  get recentOrders(): RecentOrFavoriteOrder[] {
    return this.$appStore.getters[CUSTOMER_GETTERS.RECENT_ORDERS];
  }

  get favoriteOrders(): RecentOrFavoriteOrder[] {
    return this.$appStore.getters[CUSTOMER_GETTERS.FAVORITE_ORDERS];
  }

  // #endregion

  // #region Promo Banners
  get promoBannerTopGuestDesktop(): PromoBanner | null {
    if (this.countryCode === CountryCode.CA) {
      return this.$appStore.state.cms.promoBannerTopGuestDesktopCanada;
    }
    return this.$appStore.state.cms.promoBannerTopGuestDesktop;
  }

  get promoBannerTopGuestMobile(): PromoBanner | null {
    if (this.countryCode === CountryCode.CA) {
      return this.$appStore.state.cms.promoBannerTopGuestMobileCanada;
    }
    return this.$appStore.state.cms.promoBannerTopGuestMobile;
  }

  get promoBannerTopAuthenticatedDesktop(): PromoBanner | null {
    if (this.countryCode === CountryCode.CA) {
      return this.$appStore.state.cms.promoBannerTopAuthenticatedDesktopCanada;
    }
    return this.$appStore.state.cms.promoBannerTopAuthenticatedDesktop;
  }

  get promoBannerTopAuthenticatedMobile(): PromoBanner | null {
    if (this.countryCode === CountryCode.CA) {
      return this.$appStore.state.cms.promoBannerTopAuthenticatedMobileCanada;
    }
    return this.$appStore.state.cms.promoBannerTopAuthenticatedMobile;
  }

  get promoBannerTop(): PromoBanner | null {
    if (this.isLoggedIn) {
      if (this.isDesktop) {
        return this.promoBannerTopAuthenticatedDesktop;
      } else {
        return this.promoBannerTopAuthenticatedMobile;
      }
    } else {
      if (this.isDesktop) {
        return this.promoBannerTopGuestDesktop;
      } else {
        return this.promoBannerTopGuestMobile;
      }
    }
  }

  get promoBannerBottom(): PromoBanner | null {
    if (this.countryCode === CountryCode.CA) {
      return this.$appStore.state.cms.promoBannerBottomCanada;
    }
    return this.$appStore.state.cms.promoBannerBottom;
  }

  // #endregion

  // #region Featured
  get featuredData(): PreConfiguredMeal[] {
    return this.$appStore.state.menu.preConfiguredMeals.filter(meal => meal.mealType == MealType.Influencer);
  }

  // #endregion

  // #region Loyalty Pepper
  get promotions(): Promotion[] {
    return this.$appStore.state.customer.wallet.promotions;
  }

  get countryCode(): string {
    return this.$appStore.state.ui.countryProfile.countryCode;
  }

  get hasPromotions(): boolean {
    return this.$appStore.state.customer.wallet.promotions.length > 0;
  }

  // #endregion

  // #region Group Order / Catering Promo
  get largeOrderPromo(): LargeOrderPromo | null {
    return this.$appStore.state.cms.largeOrderPromo;
  }

  get groupOrderPromo(): LargeOrderPromoCard | null {
    return this.$appStore.state.cms.groupOrderCard;
  }

  get cateringPromo(): LargeOrderPromoCard | null {
    if (this.countryCode === CountryCode.CA) {
      return this.$appStore.state.cms.cateringCardCanada;
    }
    return this.$appStore.state.cms.cateringCard;
  }

  // #endregion

  get shouldShowManageGroupOrderBanner(): boolean {
    const order = this.$services.order.getCurrentOrder();
    return !!order && order.order.orderType === OrderType.Group && this.$services.order.isCustomerHostOfGroupOrder();
  }

  get isPointsCelebrationVisible(): boolean {
    return this.$appStore.state.ui.pointsCelebrationVisible;
  }

  get shouldScrollToHash(): boolean {
    return this.$appStore.state.ui.home.shouldScrollToHash;
  }

  get isRewardsExchangeOnline(): boolean {
    return this.$appStore.state.featureFlags.orderingStatus?.isRewardsExchangeOnline ?? true;
  }

  get isRewardsOnline(): boolean {
    return this.$appStore.state.featureFlags.orderingStatus?.isRewardsOnline ?? true;
  }

  get isLoyaltyEnabled(): boolean {
    let isGuest = this.$services.customer.isLoggedIn() === false;
    return isGuest && isLoyaltyEnabled(this.$appStore.state.ui.countryProfile.countryCode);
  }

  // Display one-tap order section between rewards banner and top-level menu
  get isFeaturedSectionAboveTopLevelMenu(): boolean {
    return this.$appStore.state.adobeTarget.isFeaturedSectionAboveTopLevelMenu;
  }

  async showAEMExperienceFragments() {
    if (document.getElementById('aem-xf-hero')) {
      // If navigating back to AEM Homepage, show homepage experience fragments.
      document.getElementById('aem-xf-hero')?.classList.remove('d-none');
      document.getElementById('aem-xf-hero')?.parentElement?.classList.remove('d-none');
      document.getElementById('aem-homepage-main')?.classList.remove('d-none');
      document.getElementById('aem-xf-tout')?.classList.remove('d-none'); // TODO: Placed under #aem-homepage-main, consider removing
      document.getElementById('aem-xf-tout')?.parentElement?.classList.remove('d-none')
      document.getElementById('aem-featured-meals')?.classList.remove('d-none'); // TODO: Placed under #aem-homepage-main, consider removing
      document.getElementById('aem-xf-crowd-pleasers')?.classList.remove('d-none'); // TODO: Placed under #aem-homepage-main, consider removing
      document.getElementById('aem-xf-crowd-pleasers')?.parentElement?.classList.remove('d-none');
      this.toggleAemLoyaltyBanner();
      this.setToutXfBackgroundVideos();
      
      if (document.getElementById('aem-xf-hero')) {
        this.setHeroXfBackgroundVideo();
      }
      
    } else if (!this.isLoadingAemXfs) {
      console.log('CmgHome - hero isn\'t here, requesting all the homepage XF\'s HTML files');
      this.isLoadingAemXfs = true;
      for (let xf of AEMHomepageXfs) {
        let url = this.countryCode === CountryCode.US ? xf.url : xf.url.replace('/us/', '/' + this.countryCode.toLowerCase() + '/');
        let html = await getAemXfHtml(url, xf.rootSelector, xf.cb) as unknown as HTMLElement;
        if (html) {
          let parent = document.createElement('div');
          parent.id = xf.id;
          parent.appendChild(html as Node);
          xf.cb(parent);
        }
      }
      this.isAemHeroXFMissing = false;
      this.isLoadingAemXfs = false;
    }
  }

  hideAEMExperienceFragments() {
    // If navigating away from AEM Homepage, hide homepage experience fragments.
    document.getElementById('aem-xf-hero')?.classList.add('d-none');
    document.getElementById('aem-xf-loyalty-homepage')?.parentElement?.classList.add('d-none');
    document.getElementById('aem-homepage-main')?.classList.add('d-none');
    document.getElementById('aem-xf-tout')?.classList.add('d-none'); // TODO: Placed under #aem-homepage-main, consider removing
    document.getElementById('aem-featured-meals')?.classList.add('d-none'); // TODO: Placed under #aem-homepage-main, consider removing
    document.getElementById('aem-xf-crowd-pleasers')?.classList.add('d-none'); // TODO: Placed under #aem-homepage-main, consider removing
  }

  removeHeaderMenuLinkNavigation() {
    // Change header nav links Logo and "Menu" to stay on same page and scroll down to #menu
    let links = document.querySelectorAll('header a[href^="/home"]') as NodeListOf<HTMLAnchorElement>;
    for (let link of links) {
      link.dataset.oldLink = link.pathname + link.search + link.hash;
      if (link.href.indexOf('#') > -1 || link.href.indexOf('?') > -1) {
        link.setAttribute('href', link.search + link.hash);
      } else {
        link.removeAttribute('href');
      }
    }
  }

  resetHeaderMenuLinkNavigation() {
    // Change header nav links Logo and "Menu" to stay on same page and scroll down to #menu
    let links = document.querySelectorAll('header a[data-old-link]') as NodeListOf<HTMLAnchorElement>;
    for (let link of links) {
      link.href = link.dataset?.oldLink || document.location.href;
    }
  }

  get shouldAemLoyaltyBannerShow() {
    let banner = document.querySelector('#aem-xf-loyalty-homepage');
    if (!banner) {
      return false;
    } else {
      return true;
    }
  }

  // Ensure tout background video sources are set 
  setToutXfBackgroundVideos() {
    const toutXf = document.querySelector('#aem-xf-tout') as HTMLElement;
    if (toutXf) {
      const videos = toutXf.querySelectorAll('video');
      videos.forEach((video) => {
        const source = video.querySelector('source');
        if (source && source.dataset.src) {
          source.setAttribute('src', source.dataset.src);
          video.load();
        }
      });
    }
  }

  // Ensure hero background video sources are set 
  setHeroXfBackgroundVideo() {
    const heroXf = document.querySelector('#aem-xf-hero') as HTMLElement;
    if (heroXf) {
      const videos = heroXf.querySelectorAll('video');
      videos.forEach((video) => {
        const source = video.querySelector('source');
        if (source && source.dataset.src) {
          source.setAttribute('src', source.dataset.src);
          video.load();
        }
      });
    }
  }

  // If user is logged in, hide loyalty banner
  toggleAemLoyaltyBanner() {
    let banner = document.querySelector('#aem-xf-loyalty-homepage');
    if (banner) {
      if (this.isLoggedIn) {
        banner.parentElement?.classList.add('d-none');
      } else {
        banner.parentElement?.classList.remove('d-none');
      }
    }
  }

  private async onUserLogin() {
    this.toggleAemLoyaltyBanner();
  }

  async beforeMount() {
    // Determine if the hero is missing from page source code and needs to be dynamically requested
    this.isAemHeroXFMissing = !document.querySelector('#aem-xf-hero');
  }

  async mounted() {
    this.removeHeaderMenuLinkNavigation();
    // If navigating back to AEM Homepage, show homepage experience fragments again.
    this.showAEMExperienceFragments();

    this.watchers.push(this.$appStore.watch(state => state.menu.activeMenu, this.scrollToHash, { immediate: true }));
    this.watchers.push(this.$appStore.watch(state => state.restaurant.selectedRestaurant, this.onRestaurantSelected, { immediate: true }));
    this.watchers.push(this.$appStore.watch(state => state.customer.profile, this.onUserLogin));

    // Check if online ordering flag was turned off in APIM
    await this.$services.featureFlags.validateOnlineOrdering();

    this.sendHomePageLoadEvent();

    this.$services.adobeTarget.sendAdobeTargetExperienceRequest(this.$route.name);
    window.dispatchEvent(new Event('cmg-aem-home-mounted'));

    if (this.$appStore.state.ui.shouldBagOpen) {
      this.$services.ui.setLastSelectedElement('bag');
      Vue.nextTick(() => this.$services.ui.focusDiv('.fullModal'));
      this.$services.ui.setPCELandingRoute('', false);
    }
  }

  updated() {
    if (document.querySelector('#aem-xf-hero.d-none')) {
      window.dispatchEvent(new Event('cmg-aem-home-updated'));
      this.showAEMExperienceFragments();
    }
  }

  destroyed() {
    window.dispatchEvent(new Event('cmg-aem-home-destroyed'));
    this.hideAEMExperienceFragments();
    this.resetHeaderMenuLinkNavigation();
    for (const unsubFn of this.watchers) {
      unsubFn();
    }
  }

  // This is so components like the bag can issue a command to scroll when page is already at the correct route
  @Watch('shouldScrollToHash')
  onShouldScrollToHash(shouldScroll: boolean) {
    if (shouldScroll) {
      this.hasScrolledToHash = false;
      this.scrollToHash();
      this.$services.ui.setShouldScrollHomeToHash(false);
    }
  }

  private scrollToHash() {
    if (!this.$route.hash || this.hasScrolledToHash) {
      return;
    }
    const routeHash: string = this.$route.hash.replace('#', '');
    const componentEl = document.getElementById(routeHash);
    if (componentEl) {
      this.hasScrolledToHash = true;
      this.$nextTick(() => componentEl.scrollIntoView());
    }
  }

  private onRestaurantSelected() {
    this.$services.logger.debug('Refreshing data after restaurant selection...');

    this.refreshFavoriteOrders();
    this.refreshRecentOrders();
    this.refreshPreConfiguredMeals();
  }

  private async refreshFavoriteOrders(): Promise<any> {
    if (this.isLoadingFavorites) {
      return;
    }
    this.isLoadingFavorites = true;

    try {
      await this.$services.recentAndFavoriteOrders.refreshFavoriteOrders();
    } catch (e: any) {
      this.$services.logger.error('Something went wrong while refreshing favorite orders', undefined, e);
    } finally {
      this.isLoadingFavorites = false;
    }
  }


  private async refreshRecentOrders(): Promise<any> {
    if (this.isLoadingRecent) {
      return;
    }
    this.isLoadingRecent = true;

    try {
      await this.$services.recentAndFavoriteOrders.refreshRecentOrders();
    } catch (e: any) {
      this.$services.logger.error('Something went wrong while refreshing recent orders', undefined, e);
    } finally {
      this.isLoadingRecent = false;
    }
  }


  private async refreshPreConfiguredMeals() {
    const restaurant = this.$appStore.state.restaurant.selectedRestaurant;

    try {
      if (!restaurant) {
        await this.$services.menu.refreshPreConfiguredMeals().then(() => this.scrollToHash()); // Universal PCEs
        return;
      } else {
        await this.$services.menu.refreshPreConfiguredMeals(restaurant.restaurantNumber, true).then(() => this.scrollToHash());
      }
    } catch (e: any) {
      this.$services.logger.error('Something went wrong while fetching PreConfiguredMeal data: ', undefined, e);
    }
  }

  private sendHomePageLoadEvent(): void {
    // We only want to fire home pageLoaded event if user is navigating to menu or if it the first time they are navigating to the 'home' route.
    if (this.$router.currentRoute.hash === '#menu' || !this.$appStore.state.adobe.hasHomePageBeenViewed) {
      const query = this.$router.currentRoute.query;
      // If any utm params are provided, create external marketing campaign event
      if (query?.utm_campaign || query?.utm_content || query?.utm_content || query?.utm_medium || query?.utm_source || query?.utm_term) {
        this.$services.analytics.send(new ExternalCampaignEvent(query));
      } else {
        this.$services.analytics.send(new HomePageLoadEvent());
      }
    }
  }
}
